import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

export const useContributorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [points, setPoints] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [addressForm, setAddressForm] = useState({
    mailing_address: '',
    mailing_city: '',
    mailing_state: '',
    mailing_zip: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  const fetchContributorData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch profile
      const profileRes = await axios.get(`${API}/contributors/my-profile`, { headers });
      setProfile(profileRes.data);
      
      // Pre-fill address form
      setAddressForm({
        mailing_address: profileRes.data.mailing_address || '',
        mailing_city: profileRes.data.mailing_city || '',
        mailing_state: profileRes.data.mailing_state || '',
        mailing_zip: profileRes.data.mailing_zip || ''
      });
      
      // Fetch points
      const pointsRes = await axios.get(`${API}/contributors/my-points`, { headers });
      setPoints(pointsRes.data);
      
      // Fetch payouts
      const payoutsRes = await axios.get(`${API}/contributors/my-payouts`, { headers });
      setPayouts(payoutsRes.data);
      
    } catch (error) {
      if (error.response?.status === 404) {
        setProfile(null);
      } else {
        console.error('Failed to fetch contributor data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  const refreshPoints = useCallback(async () => {
    try {
      const pointsRes = await axios.get(`${API}/contributors/my-points`, { headers });
      setPoints(pointsRes.data);
    } catch (error) {
      console.error('Failed to refresh points:', error);
    }
  }, []);
  
  const handleSaveAddress = async () => {
    if (!addressForm.mailing_address || !addressForm.mailing_city || 
        !addressForm.mailing_state || !addressForm.mailing_zip) {
      toast.error('Please fill in all address fields');
      return;
    }
    
    setSavingAddress(true);
    try {
      await axios.put(`${API}/contributors/my-profile/address`, addressForm, { headers });
      toast.success('Address saved successfully');
      fetchContributorData();
    } catch (error) {
      toast.error('Failed to save address');
    } finally {
      setSavingAddress(false);
    }
  };
  
  useEffect(() => {
    fetchContributorData();
  }, [fetchContributorData]);
  
  return {
    loading,
    profile,
    points,
    payouts,
    addressForm,
    setAddressForm,
    savingAddress,
    refreshPoints,
    handleSaveAddress,
  };
};

// Helper functions
export const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

export const getActivityIcon = (type) => {
  const icons = {
    'login': '🔑',
    'forum_post': '📝',
    'forum_reply': '💬',
    'homework_review': '✅',
    'homework_feedback': '📋',
    'live_stream': '🎥',
    'manual_adjustment': '⚙️',
    'workshop_creation': '🎓'
  };
  return icons[type] || '•';
};

export const getActivityLabel = (type) => {
  const labels = {
    'login': 'Daily Login',
    'forum_post': 'Forum Post',
    'forum_reply': 'Forum Reply',
    'homework_review': 'Homework Review',
    'homework_feedback': 'Homework Feedback',
    'live_stream': 'Live Stream',
    'manual_adjustment': 'Manual Adjustment',
    'workshop_creation': 'Workshop Creation'
  };
  return labels[type] || type;
};
